import { useEffect, useMemo, useState } from 'react'
import { Container } from './styles'
import githubIcon from '../../assets/github.svg'
import externalLink from '../../assets/external-link.svg'

type Repo = {
  name: string
  html_url: string
  description: string | null
  language: string | null
  homepage: string | null
  stargazers_count: number
  fork: boolean
  archived: boolean
  topics?: string[]
  pushed_at: string
}

export function Project() {
  const [repos, setRepos] = useState<Repo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(
          "https://api.github.com/users/MeherSultana/repos?per_page=100&sort=updated",
          {
            headers: {
              Accept: "application/vnd.github+json",
            },
          }
        );
        if (!res.ok) throw new Error(`GitHub error ${res.status}`);
        const data: Repo[] = await res.json();
        setRepos(data);
      } catch (e: any) {
        setError(e?.message || "Failed to load repositories");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const displayRepos = useMemo(() => {
    return repos
      .filter((r) => !r.fork && !r.archived)
      .sort((a, b) => {
        if (b.stargazers_count !== a.stargazers_count)
          return b.stargazers_count - a.stargazers_count;
        return new Date(b.pushed_at).getTime() - new Date(a.pushed_at).getTime();
      })
      .slice(0, 6);
  }, [repos]);

  return (
    <Container id="project">
      <div className="section-header">
        <p className="eyebrow">Work</p>
        <h2>Projects</h2>
        <p className="muted">Pulled live from GitHub - prioritized by stars and recency.</p>
      </div>

      {loading && <p className="status muted">Loading projects...</p>}
      {error && !loading && <p className="status muted">Could not load projects.</p>}

      <div className="projects">
        {displayRepos.map((repo) => {
          const tags =
            repo.topics && repo.topics.length > 0
              ? repo.topics.slice(0, 3)
              : [repo.language].filter(Boolean)

          return (
            <div className="project" key={repo.html_url}>
              <div className="project-top">
                <span className="tag">{repo.language || 'Project'}</span>
                <div className="project-links">
                  <a href={repo.html_url} target="_blank" rel="noreferrer" aria-label="GitHub repository">
                    <img src={githubIcon} alt="" />
                  </a>
                  {repo.homepage && /^https?:\/\//.test(repo.homepage) && (
                    <a href={repo.homepage} target="_blank" rel="noreferrer" aria-label="Live demo">
                      <img src={externalLink} alt="" />
                    </a>
                  )}
                </div>
              </div>
              <h3>{repo.name.replace(/[-_]/g, ' ')}</h3>
              <p className="description">{repo.description || 'No description provided.'}</p>
              <div className="tech-list">
                {tags.map((t) => (
                  <span className="pill" key={t as string}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </Container>
  )
}
